/* Presentation only. Aris owns all inputs, conditional rules, prices and cart properties. */
(() => {
  const script = document.querySelector('script[data-swaggy-clothing]');
  if (!script) return;
  // Review-only comparison with the untouched Aris presentation.
  if (new URLSearchParams(location.search).get('swaggy_options') === 'original') return;
  const custom = script.dataset.custom === 'true';
  const states = new WeakMap();
  let sequence = 0;
  let activePreview;
  let previewFrame;
  const previewPointer = matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px)');
  const extraNames = new Set([
    'מבצעים מיוחדים בקניית בגד', 'בחירת צבע הסרט', 'כיתוב על הסרט', 'צבע כיתוב על הסרט',
    'בחירת צבע הגרביים', 'כיתוב על הגרב', 'צבע כיתוב על הגרביים',
    'בחירת כובע ג׳ינס', 'כיתוב על כובע הג׳ינס', 'צבע כיתוב על כובע הג׳ינס',
    'בחירת כובע פונפון', 'כיתוב על כובע הפונפון', 'צבע כיתוב על כובע הפונפון',
    'סוג כיפה', 'איור על הכיפה', 'כיתוב על הכיפה', 'צבע כיתוב על הכיפה',
    'כיתוב על הסינר', 'צבע כיתוב על הסינר', 'איור על מטפחת הראש',
    'כיתוב על המטפחת', 'צבע כיתוב מטפחת ראש'
  ]);
  const text = (el, value) => { if (el && el.textContent !== value) el.textContent = value; };
  const title = field => field.querySelector('.apo-title')?.textContent.trim() || '';
  const active = input => !input.disabled && !input.closest('.avp-option[disabled]');
  const checked = fields => fields.flatMap(field => [...field.querySelectorAll('input:checked')]).filter(active);
  const amount = cents => new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', maximumFractionDigits: 2 }).format(cents / 100);

  function fold(state, key, fields, label, emptySummary) {
    if (!fields.length) return;
    let group = state.groups.get(key);
    if (!group || !group.button.isConnected) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'swaggy-option-toggle';
      button.dataset.swaggyToggle = key;
      const copy = document.createElement('span');
      copy.className = 'swaggy-option-toggle-copy';
      const heading = document.createElement('span');
      heading.className = 'swaggy-option-toggle-title';
      heading.textContent = label;
      const summary = document.createElement('span');
      summary.className = 'swaggy-option-toggle-summary';
      const icon = document.createElement('span');
      icon.className = 'swaggy-option-toggle-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '+';
      copy.append(heading, summary);
      button.append(copy, icon);
      group = { button, summary, icon, fields, open: false, key, emptySummary };
      state.groups.set(key, group);
      button.addEventListener('click', () => {
        group.open = !group.open;
        updateGroup(group);
      });
    }
    group.fields = fields;
    const ids = fields.map(field => {
      if (!field.id) field.id = `swaggy-option-${++sequence}`;
      return field.id;
    });
    const controls = ids.join(' ');
    if (group.button.getAttribute('aria-controls') !== controls) group.button.setAttribute('aria-controls', controls);
    if (group.button.nextElementSibling !== fields[0]) fields[0].before(group.button);
    fields.forEach(field => { if (field.dataset.swaggyGroup !== key) field.dataset.swaggyGroup = key; });
    updateGroup(group);
  }

  function updateGroup(group) {
    const selected = checked(group.fields);
    const notes = group.fields.flatMap(field => [...field.querySelectorAll('textarea')]).find(input => active(input) && input.value.trim());
    const files = group.fields.flatMap(field => [...field.querySelectorAll('input[type="file"]')]).filter(active).flatMap(input => [...input.files]);
    let summary = group.emptySummary;
    if (group.key === 'extras' && selected.length) {
      const extras = selected.filter(input => input.type === 'checkbox');
      const price = selected.reduce((sum, input) => sum + (Number(input.dataset.variantPrice) || 0), 0);
      summary = extras.map(input => input.value).join(', ') + (price ? ` · +${amount(price)}` : '');
    } else if (notes) summary = 'נוספה בקשת עיצוב';
    else if (files.length) summary = files.map(file => file.name).join(', ');
    text(group.summary, summary);
    const folded = !group.open;
    const expanded = String(!folded);
    if (group.button.getAttribute('aria-expanded') !== expanded) group.button.setAttribute('aria-expanded', expanded);
    text(group.icon, folded ? '+' : '−');
    group.fields.forEach(field => {
      if (field.dataset.swaggyFolded !== String(folded)) field.dataset.swaggyFolded = String(folded);
    });
  }

  function grow(input) {
    if (!input.getClientRects().length) return;
    input.style.height = 'auto';
    input.style.height = `${Math.max(148, input.scrollHeight + 2)}px`;
  }

  function positionPreview(wrapper) {
    if (!previewPointer.matches || !wrapper?.isConnected) return;
    const preview = wrapper.querySelector('.apo-swatch-tooltip');
    const anchor = wrapper.querySelector('.avp-productoptionswatch-box');
    if (!preview || !anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = preview.offsetWidth;
    const height = preview.offsetHeight;
    const margin = 16;
    const left = Math.max(margin, Math.min(rect.left + rect.width / 2 - width / 2, document.documentElement.clientWidth - width - margin));
    let top = rect.top - height - 10;
    if (top < margin) top = rect.bottom + 10;
    top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));
    preview.style.setProperty('--swaggy-preview-left', `${left - rect.left}px`);
    preview.style.setProperty('--swaggy-preview-top', `${top - rect.top}px`);
    preview.style.setProperty('--swaggy-preview-bottom', 'auto');
    preview.style.setProperty('--swaggy-preview-transform', 'none');
  }

  const previewWrapper = target => {
    const wrapper = target.closest?.('.apo-swatch-wrapper');
    return wrapper?.closest('.swaggy-garment-options, .swaggy-extra-options') ? wrapper : null;
  };
  const repositionPreview = () => {
    if (!activePreview || previewFrame) return;
    previewFrame = requestAnimationFrame(() => { previewFrame = 0; positionPreview(activePreview); });
  };
  window.addEventListener('scroll', repositionPreview, { passive: true });
  window.addEventListener('resize', repositionPreview, { passive: true });

  function reconcile(root) {
    if (!root.querySelector('input[field-name="סוג הבגד"]')) return;
    let state = states.get(root);
    if (!state) {
      state = { groups: new Map() };
      states.set(root, state);
      root.dataset.swaggyClothingOptions = '';
      root.addEventListener('pointerover', event => {
        const wrapper = previewWrapper(event.target);
        if (!wrapper || wrapper.contains(event.relatedTarget)) return;
        activePreview = wrapper;
        positionPreview(wrapper);
      });
      root.addEventListener('pointerout', event => {
        if (activePreview && !activePreview.contains(event.relatedTarget)) activePreview = null;
      });
      root.addEventListener('focusin', event => {
        activePreview = previewWrapper(event.target);
        positionPreview(activePreview);
      });
      root.addEventListener('focusout', () => { activePreview = null; });
      root.addEventListener('change', () => state.groups.forEach(updateGroup));
      root.addEventListener('input', event => {
        if (event.target.matches('textarea[data-swaggy-notes]')) grow(event.target);
        state.groups.forEach(updateGroup);
      });
      const reveal = target => {
        const field = target.closest?.('[data-swaggy-group]');
        const group = field && state.groups.get(field.dataset.swaggyGroup);
        if (group && !group.open) { group.open = true; updateGroup(group); }
      };
      root.addEventListener('invalid', event => reveal(event.target), true);
      root.addEventListener('focusin', event => reveal(event.target));
      // Expand optional groups before Aris validates a submitted configuration.
      // Do not intercept submission or alter the app's validation / cart behavior.
      const form = root.closest('form');
      form?.addEventListener('click', event => {
        if (!event.target.closest('button[type="submit"], input[type="submit"]')) return;
        state.groups.forEach(group => {
          if (checked(group.fields).length) { group.open = true; updateGroup(group); }
        });
      }, true);
    }
    const fields = [...root.querySelectorAll('.avp-option')];
    const garment = root.querySelector('input[field-name="סוג הבגד"]')?.closest('.avp-option');
    garment?.classList.add('swaggy-garment-options');
    fields.forEach(field => {
      if (field.querySelector('.ap-options__button')) field.classList.add('swaggy-size-options');
      const controls = [...field.querySelectorAll('input[type="radio"], input[type="checkbox"]')];
      if (controls.some(input => /^(צבע|צבעי כיתוב)/.test(input.getAttribute('field-name') || ''))) {
        field.classList.add('swaggy-colour-options');
        controls.forEach(input => { if (!input.hasAttribute('aria-label')) input.setAttribute('aria-label', input.value); });
      }
    });
    const brief = root.querySelector('textarea[field-name="בקשות עיצוב נוספות"]');
    const briefField = brief?.closest('.avp-option');
    if (brief && !brief.hasAttribute('data-swaggy-notes')) {
      brief.dataset.swaggyNotes = '';
      brief.rows = 4;
    }
    const uploadFields = fields.filter(field => field.querySelector('input[type="file"]') && /אופציונלי/.test(title(field)) && !field.querySelector('[apo-required="true"]'));
    if (custom && brief && briefField) {
      text(briefField.querySelector('.apo-title'), 'מה תרצו שנעצב לכם?');
      briefField.classList.add('swaggy-custom-brief');
      const newBrief = !brief.hasAttribute('data-swaggy-brief');
      if (newBrief) {
        brief.dataset.swaggyBrief = '';
        brief.rows = 4;
        brief.placeholder = 'למשל: בגד ליום הולדת שנה של נועם, עם הכיתוב ״הקסם שלנו בן שנה״ ואיור של כוכבים.';
      }
      if (!briefField.querySelector('.swaggy-brief-help')) {
        const help = document.createElement('p');
        help.id = `swaggy-brief-help-${++sequence}`;
        help.className = 'swaggy-brief-help';
        help.textContent = 'ספרו לנו על הרעיון, הכיתוב והפרטים שחשוב לכם לשלב. אנחנו נכין את העיצוב ונשלח סקיצה לאישור לפני ההדפסה.';
        briefField.querySelector('.ap-label-tooltip').after(help);
      }
      const helpId = briefField.querySelector('.swaggy-brief-help').id;
      const descriptions = new Set((brief.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      descriptions.add(helpId);
      const description = [...descriptions].join(' ');
      if (brief.getAttribute('aria-describedby') !== description) brief.setAttribute('aria-describedby', description);
      // Keep document order and keyboard order aligned, without cloning inputs.
      const firstUpload = uploadFields[0];
      const uploadAnchor = state.groups.get('upload')?.button || firstUpload;
      if (uploadAnchor && (briefField.compareDocumentPosition(uploadAnchor) & Node.DOCUMENT_POSITION_PRECEDING)) uploadAnchor.before(briefField);
      if (newBrief) grow(brief);
      fold(state, 'upload', uploadFields, 'הוספת תמונה להשראה (לא חובה)', 'אפשר לצרף תמונה או סקיצה');
    } else if (briefField && !briefField.querySelector('[apo-required="true"], [required]')) {
      fold(state, 'requests', [briefField], 'בקשות עיצוב נוספות (לא חובה)', 'התאמות, צבעים ודגשים לעיצוב');
    }
    const extras = fields.filter(field => extraNames.has(title(field)));
    if (extras.some(field => title(field) === 'מבצעים מיוחדים בקניית בגד')) {
      extras[0].classList.add('swaggy-extra-options');
      // Custom design colours belong with the design brief, before accessories.
      const colours = custom && fields.find(field => title(field) === 'צבעי כיתוב');
      const extrasAnchor = state.groups.get('extras')?.button || extras[0];
      if (colours && (colours.compareDocumentPosition(extrasAnchor) & Node.DOCUMENT_POSITION_PRECEDING)) extrasAnchor.before(colours);
      fold(state, 'extras', extras, 'מבצעים בקניית בגד', 'סרטים, גרביים, כיפות ועוד');
    }
    // Preview captions repeat the original labels; keep accessible names stable on hover.
    root.querySelectorAll(':is(.swaggy-garment-options, .swaggy-extra-options) .apo-swatch-tooltip').forEach(preview => {
      if (preview.getAttribute('aria-hidden') !== 'true') preview.setAttribute('aria-hidden', 'true');
    });
    state.groups.forEach(group => {
      if (group.fields.some(field => field.querySelector('[aria-invalid="true"]'))) group.open = true;
      updateGroup(group);
    });
  }

  let queued = false;
  const scan = () => {
    queued = false;
    document.querySelectorAll('main form[id^="product-form-"][id$="__main"] .avpoptions-container__v2').forEach(reconcile);
  };
  const observer = new MutationObserver(records => {
    if (queued || !records.some(record => {
      if (record.target.closest?.('.avpoptions-container__v2')) return true;
      return [...record.addedNodes].some(node => node.nodeType === 1 &&
        (node.matches('.avpoptions-container__v2') || node.querySelector('.avpoptions-container__v2')));
    })) return;
    queued = true;
    requestAnimationFrame(scan);
  });
  const main = document.querySelector('main');
  if (main) {
    observer.observe(main, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-invalid', 'disabled'] });
    scan();
  }
})();
